import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import AdaptiveLayout from 'src/layouts/AdaptiveLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const AllGames = Loader(lazy(() => import('src/content/dashboards/AllGames')));
const GameSearch = Loader(lazy(() => import('src/content/dashboards/GameSearch')));
const GameInfo = Loader(lazy(() => import('src/content/dashboards/GameInfo')));
const AIGameInfo = Loader(lazy(() => import('src/content/dashboards/AIGameInfo')));
const CreateGame = Loader(lazy(() => import('src/content/dashboards/CreateGame')));
const Crypto = Loader(lazy(() => import('src/content/dashboards/Crypto')));
const EventDetail = Loader(lazy(() => import('src/content/dashboards/EventDetail')));

// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Transactions = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const PublicUserProfile = Loader(
  lazy(() => import('src/content/applications/Users/publicProfile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);

//#region login
//==========================

const GoogleLoginRedirectGetAuthenCodePage = Loader(
  lazy(() => import('src/content/applications/login/google/redirectAuthenCode'))
);

//#endregion

// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <AdaptiveLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/dashboards/all-games" replace />
      },
      {
        path: '/',
        element: <Navigate to="/dashboards/all-games" replace />
      },
      {
        path: 'overview',
        element: <Navigate to="/dashboards/all-games" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: <AdaptiveLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="all-games" replace />
      },
      {
        path: 'all-games',
        element: <AllGames />
      },
      {
        path: 'search',
        element: <GameSearch />
      },
      {
        path: 'games/:gameId',
        element: <GameInfo />
      },
      {
        path: 'dynamic-games/:id',
        element: <AIGameInfo />
      },
      {
        path: 'profile/:userId',
        element: <PublicUserProfile />
      },
      {
        path: 'create-game',
        element: <CreateGame />
      },
      {
        path: 'event/:eventId',
        element: <EventDetail />
      },
      {
        path: 'crypto',
        element: <Crypto />
      },
      {
        path: 'messenger',
        element: <Messenger />
      }
    ]
  },
  {
    path: 'login',
    element: <AdaptiveLayout />,
    children: [
      {
        path: 'google',
        children: [
          {
            path: 'redirect',
            element: <GoogleLoginRedirectGetAuthenCodePage />
          }
        ]
      },
    ]
  },
  {
    path: 'management',
    element: <AdaptiveLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'transactions',
        element: <Transactions />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },
  {
    path: '/components',
    element: <AdaptiveLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

export default routes;
